<script setup lang="ts">
  import type {
    LhChange,
    LhCopyValue,
    PatternlibTextinputCustomEvent
  } from '@liebherr/patternlib/dist/types/components';
  import { computed, inject, onMounted, ref, watch, type PropType } from 'vue';
  import { useI18n } from 'vue-i18n';

  //i18n
  const { t } = useI18n();

  //props
  type Type = 'serialnumber' | 'articlenumber';
  const { incompleteFormSubmitTry, type = 'serialnumber' } = defineProps({
    incompleteFormSubmitTry: Boolean,
    type: String as PropType<Type>
  });

  //variables
  const createToast: any = inject('createToast');
  const value = ref<string>('');

  //computed
  const inputStatus = computed(() => validateInput(value.value));

  //watcher
  watch(value, () => {
    emit('status-changed', inputStatus.value);
    emit('value-changed', value.value.replace(/\D/g, ''));
  });

  //emit
  const emit = defineEmits<{
    (e: 'status-changed', value: string): void;
    (e: 'value-changed', value: string): void;
  }>();

  //methods
  const validateInput = (value: string) => {
    const checkDigit = (value: string) => {
      const checkSum1 =
        parseInt(value[0]) +
        parseInt(value[2]) +
        parseInt(value[4]) +
        parseInt(value[6]);
      const checkSum2 =
        parseInt(value[1]) +
        parseInt(value[3]) +
        parseInt(value[5]) +
        parseInt(value[7]);
      const checkSum3 = 10 - ((checkSum1 + 3 * checkSum2) % 10);
      return checkSum3 === 10 ? 0 : checkSum3;
    };

    if (incompleteFormSubmitTry) return 'error';
    const valueDigits = value.replace(/\D/g, '');
    if (valueDigits.length > 0 && valueDigits.length < 9) return 'warning';
    else if (valueDigits.length == 0) return 'default';
    return parseInt(valueDigits[8]) === checkDigit(valueDigits) ||
      type === 'articlenumber'
      ? 'success'
      : 'error';
  };

  const formatInput = (input: string, tb: HTMLPatternlibTextinputElement) => {
    const inputText = input.replace(/[^0-9]/g, '').slice(0, 9);

    if (type === 'serialnumber') {
      const formattedValue = inputText.replace(
        /(\d{2})(\d{0,3})(\d{0,3})(\d?)/,
        (_match, p1, p2, p3, p4) => {
          let result = p1;
          if (p2) result += '.' + p2;
          if (p3) result += '.' + p3;
          if (p4) result += '.' + p4;
          return result;
        }
      );

      tb.value = formattedValue;
      value.value = formattedValue;
    } else {
      tb.value = inputText;
      value.value = inputText;
    }

    return inputText.length;
  };

  //mounted
  onMounted(() => {
    const patternlibTextinput =
      document.querySelector<HTMLPatternlibTextinputElement>(
        '.customTextInput'
      );

    patternlibTextinput?.addEventListener(
      'lhChange',
      (e: PatternlibTextinputCustomEvent<LhChange>) => {
        const length = formatInput(
          e.detail.value.toString(),
          patternlibTextinput
        );
        if (length === 9) {
          validateInput(value.value);
        }
      }
    );

    patternlibTextinput?.addEventListener(
      'lhCopyValue',
      async (e: PatternlibTextinputCustomEvent<LhCopyValue>) => {
        const value = e.detail?.value;
        if (!value) {
          createToast('Value is empty', null, 'info');
          return;
        }

        navigator.clipboard
          .writeText(value)
          .then(() => {
            createToast(t('clipboard.success'), null, 'success');
          })
          .catch(() => {
            createToast(t('clipboard.error'), null, 'error');
          });
      }
    );
  });
</script>

<template>
  <patternlib-textinput
    class="customTextInput"
    :label="
      type === 'serialnumber'
        ? $t('serialNumber.label')
        : $t('articleNumber.label')
    "
    :placeholder="
      type === 'serialnumber'
        ? $t('serialNumber.placeholder')
        : $t('articleNumber.placeholder')
    "
    :show-copy-icon="false"
    :status="inputStatus"
    :maxlength="12"
  >
    <div slot="info-message" v-if="inputStatus == 'default'">
      {{
        type === 'serialnumber'
          ? $t('serialNumber.validationRules.information')
          : $t('articleNumber.validationRules.information')
      }}
    </div>
    <div slot="error-message" v-else-if="inputStatus == 'error'">
      {{
        type === 'serialnumber'
          ? $t('serialNumber.validationRules.error')
          : $t('articleNumber.validationRules.error')
      }}
    </div>
    <div slot="warning-message" v-else-if="inputStatus == 'warning'">
      {{
        type === 'serialnumber'
          ? $t('serialNumber.validationRules.warning')
          : $t('articleNumber.validationRules.warning')
      }}
    </div>
    <div slot="success-message" v-else-if="inputStatus == 'success'">
      {{
        type === 'serialnumber'
          ? $t('serialNumber.validationRules.success')
          : $t('articleNumber.validationRules.success')
      }}
    </div>
  </patternlib-textinput>
</template>

<style scoped></style>
